import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import { Package, ShoppingCart, AlertCircle, X } from "lucide-react";

import PDFUploader from "./PDFUploader";
import UploadResponse from "./UploadResponse";
import FilesTable from "./FilesTable";
import ShipmentNoteDetails from "./ShipmentNoteDetails";

export default function SelfShipOrders() {
  const authToken = localStorage.getItem("authToken");
  const parentClientId = localStorage.getItem("parentClientId");
  const OMS = process.env.REACT_APP_OMS_BASE_URL;
  const POCAS = process.env.REACT_APP_POCAS_BASE_URL;

  // We currently only support “amazon”
  const MARKETPLACES = ["amazon"];

  // ─── State ───────────────────────────────────────────────────────────────
  const [marketplace, setMarketplace] = useState(MARKETPLACES[0]);
  const [files, setFiles] = useState([]);                            // List of uploaded files
  const [uploadResp, setUploadResp] = useState(null);                // Response after uploading PDF
  const [details, setDetails] = useState(null);                      // The “file details” to show in overlay
  const [pickupList, setPickupList] = useState([]);                  // Pick‐up locations for dropdown

  const [loadingFiles, setLoadingFiles] = useState(false);           // Whether FilesTable is loading
  const [loadingUpload, setLoadingUpload] = useState(false);         // Whether PDF upload is in flight
  const [loadingDetailFileId, setLoadingDetailFileId] = useState(null); // Which fileId’s details we’re fetching

  const [showOverlay, setShowOverlay] = useState(false);             // Controls full‐screen overlay

  const [uploadError, setUploadError] = useState(null);              // Any “already uploaded” error
  const [configError, setConfigError] = useState(false);

  // ─── 1) Fetch all uploaded files for current marketplace ────────────────
  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const { data } = await axios.get(
        `${OMS}/v1/oms/merchant/external-shipment-notes/all-uploaded-files/${marketplace}`,
        { headers: { Authorization: authToken } }
      );
      setFiles(data);
    } catch (e) {
      console.error("Could not load files", e);
    } finally {
      setLoadingFiles(false);
    }
  }, [marketplace, OMS, authToken]);

  // ─── 2) Fetch pickup details once (to populate Pickup dropdowns) ─────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${POCAS}/v1/pocas/onboarding/${parentClientId}/getpickupdetails`,
          { headers: { Authorization: authToken } }
        );
        setPickupList(data);
      } catch (e) {
        console.warn("No pickup locations available", e);
      }
    })();
  }, [POCAS, parentClientId, authToken]);

  // ─── 3) Whenever “marketplace” changes, refresh the files list ───────────
  useEffect(() => {
    fetchFiles();
    setUploadResp(null);
    setDetails(null);
    setShowOverlay(false);
  }, [fetchFiles]);

  // ─── 4) Handle PDF upload (called by PDFUploader) ────────────────────────
  const handleUpload = async (file) => {
    if (!file) return;
    setLoadingUpload(true);
    setUploadError(null);
    setConfigError(false);

    const form = new FormData();
    form.append("shipmentNotesFile", file);

    const uploadToast = toast.loading("Uploading file...", {
      position: "bottom-right",
    });

    try {
      const { data } = await axios.post(
        `${OMS}/v1/oms/merchant/external-shipment-notes/${marketplace}/file-upload`,
        form,
        {
          headers: {
            Authorization: authToken,
          },
        }
      );
      setUploadResp(data);
      toast.update(uploadToast, {
        render: "✅ File uploaded successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      // After successful upload, refresh files list
      await fetchFiles();
    } catch (e) {
      const resp = e.response?.data;
      if (resp?.code === "merchant_config_not_found") {
        setConfigError(true);
        toast.update(uploadToast, {
          render: "❌ Configuration not found",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
      // Handle "file already uploaded" code specifically
      if (resp?.code === "merchant_external_shipment_notes_file_already_uploaded") {
        setUploadError(resp.message);
        toast.update(uploadToast, {
          render: "⚠️ File already uploaded",
          type: "warning",
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        console.error("Upload failed", e);
        toast.update(uploadToast, {
          render: "❌ Upload failed. Please try again.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  // ─── 5) Load details for one specific file (and open overlay) ────────────
  const loadDetails = async (fileId) => {
    setLoadingDetailFileId(fileId);
    try {
      const { data } = await axios.get(
        `${OMS}/v1/oms/merchant/external-shipment-notes/file-processing-details/${fileId}`,
        { headers: { Authorization: authToken } }
      );
      setDetails(data);
      setShowOverlay(true);
    } catch (e) {
      console.error("Could not load file details", e);
    } finally {
      setLoadingDetailFileId(null);
    }
  };

  // ─── 6) Single‐row “batch update” (called by ShipmentDetails’s modal “Save”) ─
  const handleRowUpdate = async (seq, changeObj) => {
    if (!details) return;
    // Find the shipment note’s order_id by seq
    const note = details.shipment_notes.find(
      (s) => s.shipment_note_sequence_number === seq
    );
    if (!note) return;

    // Build the payload: include every field the API expects
    const payload = [
      {
        shipment_notes_order_data: {
          // Keep unmodified bits from the original note
          ...note.shipment_notes_order_data,
          ...changeObj,
          order_processing_status: note.order_processing_status,
          shipment_note_sequence_number: note.shipment_note_sequence_number,
        },
      },
    ];

    try {
      await axios.put(
        `${OMS}/v1/oms/merchant/external-shipment-notes/batch-update`,
        payload,
        { headers: { Authorization: authToken } }
      );
      // Refresh the overlay contents
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Row update failed", e);
    }
  };

  // ─── 7) Per‐row Verify / Unverify ───────────────────────────────────────
  const singleVerify = async (order_id, endpointPath) => {
    if (!details) return;
    try {
      await axios.put(
        `${OMS}/${endpointPath}`,
        [{ order_id }],
        { headers: { Authorization: authToken } }
      );
      // Refresh overlay after verifying/unverifying
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Verify/unverify failed", e);
    }
  };

  // ─── 8) Batch‐Verify (selected rows in ShipmentDetails) ──────────────────
  const handleBatchVerify = async (orderIds) => {
    if (!details || orderIds.length === 0) return;
    try {
      await axios.put(
        `${OMS}/v1/oms/merchant/external-shipment-notes/batch-verify`,
        orderIds.map((oid) => ({ order_id: oid })),
        { headers: { Authorization: authToken } }
      );
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Batch verify failed", e);
    }
  };
  
  const handleBatchUpdateRows = async (payload) => {
  await axios.put(
    `${OMS}/v1/oms/merchant/external-shipment-notes/batch-update`,
    payload,
    { headers: { Authorization: authToken } }
  );
  await loadDetails(details.file_id);
};

  // ─── 9) Submit Verified Orders (final step) ─────────────────────────────
  const handleSubmit = async () => {
    if (!details) return;
    try {
      await axios.put(
        `${OMS}/v1/oms/merchant/external-shipment-notes/initiate-create-order-for-verified-shipment-notes/${details.file_id}`,
        {},
        { headers: { Authorization: authToken } }
      );
      // Refresh both the files list and the overlay contents
      await fetchFiles();
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Submit Verified Orders failed", e);
    }
  };

  // ─── 10) Apply Default Pickup to All Rows ────────────────────────────────
  const applyDefaultPickup = async (selectedPickup) => {
    if (!details || !selectedPickup) return;

    // 1) Locally update all rows’ pickup in state so the user sees it instantly
    const updated = {
      ...details,
      shipment_notes: details.shipment_notes.map((sn) => ({
        ...sn,
        shipment_notes_order_data: {
          ...sn.shipment_notes_order_data,
          pickup_address_data: selectedPickup,
        },
      })),
    };
    setDetails(updated);

    // 2) Build a batch‐update payload that updates every row’s pickup_address_data
    const payload = updated.shipment_notes.map((sn) => ({
      shipment_notes_order_data: {
        ...sn.shipment_notes_order_data,
        pickup_address_data: selectedPickup,
        order_processing_status: sn.order_processing_status,
        shipment_note_sequence_number: sn.shipment_note_sequence_number,
      },
    }));

    // 3) Call batch‐update for all rows
    try {
      await axios.put(
        `${OMS}/v1/oms/merchant/external-shipment-notes/batch-update`,
        payload,
        { headers: { Authorization: authToken } }
      );
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Batch update for default pickup failed", e);
    }
  };

  // ─── 11) Apply Default Payment Mode (PREPAID/COD) to All Rows ───────────
  const applyDefaultPayment = async (selectedPayment) => {
    if (!details || !selectedPayment) return;

    // Immediately update local state
    const updated = {
      ...details,
      shipment_notes: details.shipment_notes.map((sn) => ({
        ...sn,
        shipment_notes_order_data: {
          ...sn.shipment_notes_order_data,
          order_type: selectedPayment,
        },
      })),
    };
    setDetails(updated);

    // Batch payload
    const payload = updated.shipment_notes.map((sn) => ({
      shipment_notes_order_data: {
        ...sn.shipment_notes_order_data,
        order_type: selectedPayment,
        order_processing_status: sn.order_processing_status,
        shipment_note_sequence_number: sn.shipment_note_sequence_number,
      },
    }));

    try {
      await axios.put(
        `${OMS}/v1/oms/merchant/external-shipment-notes/batch-update`,
        payload,
        { headers: { Authorization: authToken } }
      );
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Batch update for payment failed", e);
    }
  };

  // ─── 12) Apply Default Shipment Order Type (FORWARD/REVERSE) to All Rows ──
  const applyDefaultShipmentOrderType = async (selectedShipmentType) => {
    if (!details || !selectedShipmentType) return;

    // Locally update
    const updated = {
      ...details,
      shipment_notes: details.shipment_notes.map((sn) => ({
        ...sn,
        shipment_notes_order_data: {
          ...sn.shipment_notes_order_data,
          shipment_order_type: selectedShipmentType,
        },
      })),
    };
    setDetails(updated);

    // Batch payload
    const payload = updated.shipment_notes.map((sn) => ({
      shipment_notes_order_data: {
        ...sn.shipment_notes_order_data,
        shipment_order_type: selectedShipmentType,
        order_processing_status: sn.order_processing_status,
        shipment_note_sequence_number: sn.shipment_note_sequence_number,
      },
    }));

    try {
      await axios.put(
        `${OMS}/v1/oms/merchant/external-shipment-notes/batch-update`,
        payload,
        { headers: { Authorization: authToken } }
      );
      loadDetails(details.file_id);
    } catch (e) {
      console.error("Batch update for shipment order type failed", e);
    }
  };
  

  // ──────────────────────────────────────────────────────────────────────────
  // ─── Render ──────────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
      {/* Toast Container */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="max-w-[1600px] mx-auto p-2 lg:p-2">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Marketplace Self-Ship Orders
              </h3>
              <p className="text-gray-600 mt-1">Upload and manage your marketplace shipment notes</p>
            </div>
          </div>
        </motion.div>

        {/* ─── Marketplace Tabs ─────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-6"
        >
          <div className="bg-white rounded-2xl shadow-md p-2 inline-flex gap-2">
            {MARKETPLACES.map((m) => (
              <motion.button
                key={m}
                onClick={() => setMarketplace(m)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  marketplace === m
                    ? "bg-indigo-600 text-white shadow-lg"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {marketplace === m && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-indigo-600 rounded-xl -z-10"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span>{m.charAt(0).toUpperCase() + m.slice(1)}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* ─── PDF Upload Section ────────────────────────────────────────────── */}
        <div className="mb-8">
          <PDFUploader onUpload={handleUpload} uploading={loadingUpload} />
          {uploadResp && <UploadResponse data={uploadResp} />}
        </div>

        {/* ─── Files Table ──────────────────────────────────────────────────── */}
        <FilesTable
          files={files}
          selectedFileId={details ? details.file_id : null}
          onEdit={loadDetails}
          loading={loadingFiles}
          loadingDetailFileId={loadingDetailFileId}
        />

        {/* ─── Upload Error Modal (if file already exists) ──────────────────── */}
        <AnimatePresence>
          {uploadError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
              onClick={() => setUploadError(null)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Error</h3>
                    <p className="text-gray-700">{uploadError}</p>
                  </div>
                  <button
                    onClick={() => setUploadError(null)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setUploadError(null)}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                  >
                    Got it
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Merchant Config Missing Popup ────────────────────────────────────────── */}
        <AnimatePresence>
          {configError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-xl">
                      <AlertCircle className="w-8 h-8 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        Configuration Required
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">Setup needed to continue</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfigError(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6 p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <p className="text-gray-700 leading-relaxed">
                    To use this feature, please reach out to our support team:<br /><br />
                    📧 Email us at{" "}
                    <a
                      href="mailto:support@wayuvega.com"
                      className="text-indigo-600 hover:text-indigo-700 font-semibold underline"
                    >
                      support@wayuvega.com
                    </a>
                    <br />
                    💬 Or contact via your Wayuvega WhatsApp group
                  </p>
                </div>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setConfigError(false)}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                  >
                    Close
                  </motion.button>
                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href="mailto:support@wayuvega.com"
                    className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-center"
                  >
                    Contact Support
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Full‐Screen Overlay (ShipmentDetails) ─────────────────────────── */}
        <AnimatePresence>
          {showOverlay && details && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex"
            >
              {/* Backdrop blur */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setShowOverlay(false);
                  setDetails(null);
                }}
                className="absolute inset-0 bg-black/40 backdrop-blur-md"
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="relative flex-1 bg-white overflow-y-auto shadow-2xl"
              >
                {/* A small "X" in top‐right to close */}
                <button
                  onClick={() => {
                    setShowOverlay(false);
                    setDetails(null);
                  }}
                  className="fixed top-6 right-6 z-10 p-3 bg-white rounded-full hover:bg-gray-100 transition shadow-lg border border-gray-200 group"
                >
                  <X className="w-6 h-6 text-gray-600 group-hover:text-gray-900" />
                </button>

                {/* Pass all extra handlers down into ShipmentDetails */}
                <ShipmentNoteDetails
                  details={details}
                  pickupList={pickupList}
                  loadingDetails={loadingDetailFileId === details.file_id}
                  onUseDefault={applyDefaultPickup}
                  onUpdateRow={handleRowUpdate}
                  onVerify={(id) =>
                    singleVerify(
                      id,
                      "v1/oms/merchant/external-shipment-notes/batch-verify"
                    )
                  }
                  onUnverify={(id) =>
                    singleVerify(
                      id,
                      "v1/oms/merchant/external-shipment-notes/batch-unverify"
                    )
                  }
                  onBatchVerify={handleBatchVerify}
                  onSubmit={handleSubmit}
                  onApplyPaymentAll={applyDefaultPayment}
                  onApplyShipmentOrderTypeAll={applyDefaultShipmentOrderType}
                  onBatchUpdateRows={handleBatchUpdateRows}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
